import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Hammer, Cpu, Check } from "lucide-react";
import DynamicImage from "@/components/common/DynamicImage";
import SEO from "@/components/common/SEO";
import { fadeUp } from "@/lib/motion";
import { getPageSeo } from "@/lib/seo";

const SERVICES = [
  {
    n: "01",
    icon: Building2,
    title: "Real Estate Development",
    description:
      "Master-planning residential townships, commercial spaces, and premium plotting projects — from land assembly to occupancy.",
    points: [
      "Township and master-plan design",
      "Premium NA plotting and land development",
      "Commercial real estate planning",
      "Legal, regulatory, and TP approvals",
    ],
    mediaKey: "service_1",
    imageAlt: "Architectural rendering of a master-planned community",
  },
  {
    n: "02",
    icon: Hammer,
    title: "Construction & Architecture",
    description:
      "Delivering exceptional structural quality using expert builders, modern architectural design, and rigorous quality assurance.",
    points: [
      "Modern architectural design",
      "Civil & structural engineering",
      "Material specification & quality control",
      "Project management & on-time delivery",
    ],
    mediaKey: "service_2",
    imageAlt: "Premium construction site with cranes at golden hour",
  },
  {
    n: "03",
    icon: Cpu,
    title: "Asset Management & PropTech",
    description:
      "Smart investment consulting, property management, and modern technology platforms to maximize client ROI across the asset lifecycle.",
    points: [
      "Investment advisory & portfolio strategy",
      "Property management & leasing",
      "Smart-asset tech platforms (PropTech)",
      "Rental yield optimisation & exit planning",
    ],
    mediaKey: "service_3",
    imageAlt: "Sleek workspace with charts — asset management studio",
    accentEmerald: true,
  },
];

const ServicesPage = () => {
  const seo = getPageSeo("services");
  return (
    <>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
      {/* HEADER */}
      <section data-testid="services-hero" className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 hero-overlay opacity-80" />
        <div className="container-narrow relative">
          <motion.p {...fadeUp} className="text-[11px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]">
            <span className="gold-divider" /> Services
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl"
          >
            Comprehensive Real Estate <span className="italic text-white/85">Solutions.</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed"
          >
            From visionary land acquisition to white-glove asset stewardship — Seetai
            Properties operates as a fully-integrated real estate institution.
          </motion.p>
        </div>
      </section>

      {/* Z-PATTERN SERVICES */}
      <section data-testid="services-list" className="luxe-section">
        <div className="container-narrow space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isRev = i % 2 === 1;
            return (
              <motion.div
                key={s.n}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.05 }}
                data-testid={`service-${s.n}`}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
              >
                <div className={`lg:col-span-7 ${isRev ? "lg:order-2" : ""}`}>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-[20px] bg-gradient-to-tr from-[color:rgba(212,175,55,0.16)] to-transparent blur-2xl" />
                    <DynamicImage
                      mediaKey={s.mediaKey}
                      alt={s.imageAlt}
                      ratio="aspect-[16/10]"
                      className="rounded-[20px] border border-white/10 neon-border shadow-[var(--sp-shadow)]"
                    />
                    <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-md border border-white/15 px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase text-white/80">
                      Service {s.n}
                    </div>
                  </div>
                </div>
                <div className={`lg:col-span-5 ${isRev ? "lg:order-1" : ""}`}>
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full border ${
                      s.accentEmerald
                        ? "border-[color:rgba(4,99,7,0.45)] bg-[color:var(--sp-emerald-soft)]"
                        : "border-[color:rgba(212,175,55,0.45)] bg-[color:var(--sp-gold-soft)]"
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 ${
                        s.accentEmerald ? "text-[color:var(--sp-emerald)]" : "text-[color:var(--sp-gold)]"
                      }`}
                    />
                  </div>
                  <p className="text-[11px] tracking-[0.32em] uppercase text-white/55 mt-4">
                    {s.n} — Capability
                  </p>
                  <h2 className="font-display text-4xl md:text-5xl mt-3 leading-[1.08]">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed">
                    {s.description}
                  </p>
                  <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-white/80">
                        <span className="mt-1 flex h-4 w-4 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.5)]">
                          <Check className="h-2.5 w-2.5 text-[color:var(--sp-gold)]" />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-${s.n}-cta`}
                    className="btn-ghost mt-9 inline-flex items-center gap-2 px-6 py-3 text-[12px] tracking-[0.22em] uppercase"
                  >
                    Discuss this service <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="luxe-section bg-[#0a0a0a]">
        <div className="container-narrow">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden rounded-[24px] border border-white/10 bg-gradient-to-br from-[#121212] to-[#0a0a0a] p-10 md:p-16 text-center"
          >
            <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[color:rgba(212,175,55,0.12)] blur-3xl" />
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Get in touch
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.08] max-w-3xl mx-auto">
              Ready to start a conversation about your next investment?
            </h2>
            <div className="mt-8 flex justify-center">
              <Link
                to="/contact"
                data-testid="services-closing-cta"
                className="btn-gold inline-flex items-center gap-2 px-7 py-4 text-[12px] tracking-[0.22em] uppercase"
              >
                Speak to our team <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
